import { UseMutateAsyncFunction } from "@tanstack/react-query";
import clsx from "clsx";
import addDays from "date-fns/addDays";
import React, { useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import IconButton from "@material-ui/core/IconButton";

import DateRangeSelect from "components/DateRangeSelect";
import { getDateRangeForStore } from "components/DateRangeSelect/helpers";
import ErrorBoundary from "components/ErrorBoundary";
import DownloadIcon from "components/Icons/Download";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import AssignToVetButton from "components/VetExamsAssignment/AssignToVetButton/AssignToVetButton";
import ConfirmAssignmentDialog from "components/VetExamsAssignment/ConfirmAssignmentDialog";
import { filterVisibleCheckedWorkoutIds } from "components/VetExamsTable/helper";
import { IExamsTableRowData } from "components/VetExamsTable/types";
import { useRacehorse360Api, useSetQueryData } from "hooks/api";
import { FirstApiError } from "hooks/api/errors";
import { useAppSelector } from "hooks/redux/useAppSelector";
import DateRange, { DateRangeType } from "interfaces/DateRange";
import {
  clearAssignCheckedIds,
  setSelectedDateRange
} from "store/actions/preRaceScreeningPage";
import {
  checkCurrentRaceEntriesTableTab,
  getConvertedToDateSelectedDateRange
} from "../../helper";
import {
  commonRequestedFields,
  PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY
} from "../../options";

import useStyles from "./styles";

interface IProps {
  className?: string;
  tableRows: IExamsTableRowData[];
}

export const BULK_ASSIGN_UPCOMING_RACE_ENTRY_EXAMS_KEY =
  "BULK_ASSIGN_UPCOMING_RACE_ENTRY_EXAMS_KEY";
export const BULK_ASSIGN_UPCOMING_RACE_ENTRY_RACE_DAY_EXAMS_KEY =
  "BULK_ASSIGN_UPCOMING_RACE_ENTRY_RACE_DAY_EXAMS_KEY";

const dateFilterOptions = [DateRangeType.UpcomingDates, DateRangeType.Custom];

const PickerFilter = (props: IProps) => {
  const { className, tableRows } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const { showSuccessSnack, showErrorSnack } = useSnackbar();

  const { currentUser } = useLoggedInUser();
  const { updateUpcomingRaceEntries, filterUpcomingRaceEntries } =
    useSetQueryData();

  const {
    selectedTab,
    assignCheckedIds,
    selectedFacility,
    selectedDateRange,
    showAssignedToMeOnly
  } = useAppSelector(state => state.preRaceScreeningPage, shallowEqual);

  const [isAssignToVetDialogOpen, setIsAssignToVetDialogOpen] = useState(false);

  const {
    useBulkAssignUpcomingRaceEntryExams,
    useBulkRequireAndAssignUpcomingRaceEntryRaceDayExams
  } = useRacehorse360Api();

  const { mutateAsync: assignUpcomingRaceEntryExams } =
    useBulkAssignUpcomingRaceEntryExams({
      mutationKey: [BULK_ASSIGN_UPCOMING_RACE_ENTRY_EXAMS_KEY]
    });

  const { mutateAsync: assignUpcomingRaceEntryRaceDayExams } =
    useBulkRequireAndAssignUpcomingRaceEntryRaceDayExams({
      mutationKey: [BULK_ASSIGN_UPCOMING_RACE_ENTRY_RACE_DAY_EXAMS_KEY]
    });

  const { isRaceDayTab, isEntryExamsTab, isRaceDayResultsTab } =
    checkCurrentRaceEntriesTableTab(selectedTab.value);

  const shouldShowAssignButton = [isEntryExamsTab, isRaceDayTab].some(Boolean);
  const hasCheckedAssignCheckboxesOnPage = tableRows?.some(row =>
    assignCheckedIds.includes(row.eventId)
  );
  const visibleAssignCheckedCheckBoxes = filterVisibleCheckedWorkoutIds(
    tableRows,
    assignCheckedIds
  );

  const handleOpenAssignmentDialog = () => {
    setIsAssignToVetDialogOpen(true);
  };

  const handleCloseAssignmentDialog = () => {
    setIsAssignToVetDialogOpen(false);
  };

  const handleConfirmAssignmentDialog = (assignedVetId: string) => {
    setIsAssignToVetDialogOpen(false);

    let actualRequest: UseMutateAsyncFunction<
      Partial<Omit<racehorse360.ListUpcomingRaceEntriesResponse, "toJSON">>,
      FirstApiError,
      | racehorse360.IBulkAssignUpcomingRaceEntryExamsRequest
      | racehorse360.IBulkRequireAndAssignUpcomingRaceEntryRaceDayExamsRequest,
      unknown
    >;

    if (isEntryExamsTab) {
      actualRequest = assignUpcomingRaceEntryExams;
    } else if (isRaceDayTab) {
      actualRequest = assignUpcomingRaceEntryRaceDayExams;
    }

    actualRequest &&
      actualRequest({
        ids: assignCheckedIds,
        assignedVetId,
        getOptions: {
          select: [
            ...commonRequestedFields,
            ...selectedTab.additionalRequestedFields
          ]
        }
      })
        .then(data => {
          const updatedEntries = data.upcomingRaceEntries;
          const isAssignedToAnotherVet = assignedVetId !== currentUser.rh360Id;

          if (showAssignedToMeOnly && isAssignedToAnotherVet) {
            filterUpcomingRaceEntries(
              updatedEntries.map(entry => entry.id),
              PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY
            );
          } else if (updatedEntries.length) {
            updatedEntries.forEach(entry => {
              const updatingEntryFields: racehorse360.IUpcomingRaceEntry =
                isEntryExamsTab
                  ? { entryExam: entry.entryExam }
                  : { raceDayExam: entry.raceDayExam };

              updateUpcomingRaceEntries([entry.id], updatingEntryFields);
            });
          }

          dispatch(clearAssignCheckedIds());
          showSuccessSnack("Vet Assigned");
        })
        .catch(error => {
          showErrorSnack(error.message);
        });
  };

  const handleDateRangeChange = (dateRange: DateRange) => {
    const storeDateRange = getDateRangeForStore(dateRange);

    dispatch(setSelectedDateRange(storeDateRange));
  };

  return (
    <div className={clsx(className, classes.pickerFilter)}>
      <div>
        {!(isRaceDayTab || isRaceDayResultsTab) && (
          <ErrorBoundary>
            <DateRangeSelect
              dateOptions={dateFilterOptions}
              selectedDateRange={getConvertedToDateSelectedDateRange(
                selectedDateRange
              )}
              defaultSelectedDateRange={DateRangeType.UpcomingDates}
              onChange={handleDateRangeChange}
              minDate={addDays(new Date(), 1)}
            />
          </ErrorBoundary>
        )}
      </div>

      <div className={classes.rightSide}>
        {shouldShowAssignButton && (
          <AssignToVetButton
            className={classes.assignButton}
            disabled={!hasCheckedAssignCheckboxesOnPage}
            onClick={handleOpenAssignmentDialog}
          />
        )}

        <IconButton className={classes.downloadButton} size="small">
          <DownloadIcon className={classes.downloadIcon} />
        </IconButton>

        {isAssignToVetDialogOpen && (
          <ConfirmAssignmentDialog
            open={isAssignToVetDialogOpen}
            onClose={handleCloseAssignmentDialog}
            onConfirm={handleConfirmAssignmentDialog}
            selectedItemsAmount={visibleAssignCheckedCheckBoxes.length}
            selectedFacilityId={selectedFacility?.id}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(PickerFilter);
